/**
 * Category Color Presets — flat party categories (vendor/customer classification)
 *
 * Centralized decorative color palette for the FLAT category domain
 * (`CategoryListItem`, numeric `categoryId`) — NOT the 5-level product taxonomy.
 *
 * These hex values were previously duplicated inline inside `CategoryFormModal`
 * and `CategorySelectWithCreate` (each carried its own `RECOMMENDED_COLORS`/preset
 * array, including a banned stale purple literal). They now live here once,
 * referencing the canonical brand + status colors from `colors.ts` wherever an
 * equivalent exists; the remaining purely-decorative hues (blue/cyan/magenta/geek
 * blue) have no `colors.ts` token and are defined here as their single source.
 *
 * The category `color` field is a per-category decorative tag color the user picks
 * from a `ColorPicker`; these presets are convenience suggestions, not brand tokens.
 */

import { purpleColors, statusColors } from '@/styles/colors';

/**
 * Default category color = the brand primary purple (static reference).
 * Replaces the stale hardcoded purple literal. Components with a live theme should
 * prefer `token.colorPrimary` (light/dark aware); use this constant only for
 * static defaults (schema defaults, non-themed fallbacks).
 */
export const DEFAULT_CATEGORY_COLOR = purpleColors.purple6;

// Decorative accent hues with no canonical token in colors.ts (single source here).
const CATEGORY_ACCENT_BLUE = '#1890FF';
const CATEGORY_ACCENT_CYAN = '#13C2C2';
const CATEGORY_ACCENT_MAGENTA = '#EB2F96';
const CATEGORY_ACCENT_GEEKBLUE = '#2F54EB';

/**
 * Recommended preset palette shown in the category `ColorPicker` and used to
 * auto-assign a color on inline create. Brand + status hues resolve from
 * `colors.ts`; decorative accents are the local constants above.
 */
export const CATEGORY_COLOR_PRESETS: readonly string[] = [
  purpleColors.purple6, // Primary (brand purple)
  CATEGORY_ACCENT_BLUE, // Blue
  statusColors.success, // Green
  statusColors.warning, // Orange
  statusColors.error, // Red
  CATEGORY_ACCENT_CYAN, // Cyan
  CATEGORY_ACCENT_MAGENTA, // Magenta
  CATEGORY_ACCENT_GEEKBLUE, // Geek Blue
] as const;
